import type { Language } from '@seek/melways-sites';

import type {
  GroupedSuggestionItem,
  HomeLocationFacet,
} from './LocationFilter';
import { flattenLocations } from './flattenLocations';

type GroupLocationsByMarketOrderAndMapCount = (props: {
  locations: HomeLocationFacet[];
  language: Language;
  jobPostingCountry: string;
  restOfWorldLabel: string;
}) => {
  unfilteredGroupedLocations: GroupedSuggestionItem[];
  locationCountMap: Record<string, number>;
};

export const groupLocationsByMarketOrderAndMapCount: GroupLocationsByMarketOrderAndMapCount =
  ({ locations, language, jobPostingCountry, restOfWorldLabel }) => {
    const jobPostingCountryLowerCased = jobPostingCountry.toLowerCase();

    let jobPostingLocationFacet: HomeLocationFacet | undefined;

    const seekMarketLocationFacets: HomeLocationFacet[] = [];
    const restOfWorldLocationFacets: HomeLocationFacet[] = [];

    locations.forEach((location) => {
      const locationCountry = location.homeLocation.country.toLowerCase();
      if (locationCountry === jobPostingCountryLowerCased) {
        jobPostingLocationFacet = location;
      } else if (location.homeLocation.isSeekMarketCountry) {
        seekMarketLocationFacets.push(location);
      } else {
        restOfWorldLocationFacets.push(location);
      }
    });

    const alphabeticalSort = (a: HomeLocationFacet, b: HomeLocationFacet) =>
      a.homeLocation.displayDescription.localeCompare(
        b.homeLocation.displayDescription,
        language,
      );

    seekMarketLocationFacets.sort(alphabeticalSort);
    restOfWorldLocationFacets.sort(alphabeticalSort);

    const groupedLocations: GroupedSuggestionItem[] = [];
    const countMap: Record<string, number> = {};

    const seekMarketGroupedSuggestions: GroupedSuggestionItem[] =
      seekMarketLocationFacets.map((location) => {
        const label = location.homeLocation.displayDescription;
        return {
          label,
          suggestions: flattenLocations({
            type: 'with-levels',
            location,
            level: 0,
            group: label,
          }),
        };
      });

    const restOfWorldGroupSuggestions: GroupedSuggestionItem = {
      label: restOfWorldLabel,
      suggestions: restOfWorldLocationFacets.flatMap((location) =>
        flattenLocations({
          type: 'with-levels',
          location,
          level: 0,
          group: restOfWorldLabel,
        }),
      ),
    };

    if (jobPostingLocationFacet) {
      const label = jobPostingLocationFacet.homeLocation.displayDescription;
      groupedLocations.push({
        label,
        suggestions: flattenLocations({
          type: 'with-levels',
          location: jobPostingLocationFacet,
          level: 0,
          group: label,
        }),
      });
    }

    if (seekMarketGroupedSuggestions.length > 0) {
      seekMarketGroupedSuggestions.forEach((market) => {
        if (market.suggestions.length > 0) {
          groupedLocations.push(market);
        }
      });
    }

    if (restOfWorldGroupSuggestions.suggestions.length > 0) {
      groupedLocations.push(restOfWorldGroupSuggestions);
    }

    groupedLocations.forEach((groupedLocation) => {
      groupedLocation.suggestions.forEach((location) => {
        countMap[location.id] = location.count;
      });
    });

    return {
      unfilteredGroupedLocations: groupedLocations,
      locationCountMap: countMap,
    };
  };
